import { normalizeServerBaseUrl } from './ServerApi';

export interface RealtimePresenceUser {
  userId: string;
  email: string;
  pageId: string | null;
  nodeIds: string[];
  permission: string;
  cursor: { x: number; y: number } | null;
}

export interface RealtimeEvent {
  type: string;
  data: Record<string, unknown>;
}

interface Config {
  baseUrl: string;
  token: string;
  projectId: string;
  onEvent?: (event: RealtimeEvent) => void;
  onPresence?: (users: RealtimePresenceUser[]) => void;
  onConnection?: (connected: boolean) => void;
}

function normalizePresence(value: unknown): RealtimePresenceUser[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Record<string, unknown>;
    if (typeof row.userId !== 'string' || typeof row.email !== 'string') return [];
    return [{
      userId: row.userId,
      email: row.email,
      pageId: typeof row.pageId === 'string' ? row.pageId : null,
      nodeIds: Array.isArray(row.nodeIds) ? row.nodeIds.filter((id): id is string => typeof id === 'string').slice(0, 20) : [],
      permission: typeof row.permission === 'string' ? row.permission : 'view',
      cursor: row.cursor && typeof row.cursor === 'object' && Number.isFinite(Number((row.cursor as Record<string, unknown>).x)) && Number.isFinite(Number((row.cursor as Record<string, unknown>).y))
        ? { x: Number((row.cursor as Record<string, unknown>).x), y: Number((row.cursor as Record<string, unknown>).y) } : null,
    }];
  });
}

export class RealtimeProjectClient {
  private readonly baseUrl: string;
  private controller: AbortController | null = null;
  private heartbeatTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private cursorTimer: number | null = null;
  private stopped = true;
  private presence: { pageId: string | null; nodeIds: string[]; cursor: { x: number; y: number } | null } = { pageId: null, nodeIds: [], cursor: null };
  private reconnectDelay = 1500;

  constructor(private readonly config: Config) { this.baseUrl = normalizeServerBaseUrl(config.baseUrl); }

  start() {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
    void this.sendPresence();
    this.heartbeatTimer = window.setInterval(() => void this.sendPresence(), 15_000);
  }

  stop() {
    this.stopped = true;
    this.controller?.abort(); this.controller = null;
    if (this.heartbeatTimer !== null) window.clearInterval(this.heartbeatTimer);
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    if (this.cursorTimer !== null) window.clearTimeout(this.cursorTimer);
    this.heartbeatTimer = null; this.reconnectTimer = null; this.cursorTimer = null;
    this.config.onConnection?.(false);
  }

  setPresence(pageId: string | null, nodeIds: string[], cursor: { x: number; y: number } | null = this.presence.cursor) {
    this.presence = { pageId, nodeIds: nodeIds.slice(0, 20), cursor };
    if (!this.stopped) void this.sendPresence();
  }

  setCursor(cursor: { x: number; y: number } | null) {
    this.presence = { ...this.presence, cursor };
    if (this.stopped || this.cursorTimer !== null) return;
    this.cursorTimer = window.setTimeout(() => { this.cursorTimer = null; void this.sendPresence(); }, 90);
  }

  private scheduleReconnect() {
    if (this.stopped || this.reconnectTimer !== null) return;
    const wait = this.reconnectDelay;
    this.reconnectDelay = Math.min(15_000, Math.round(this.reconnectDelay * 1.6));
    this.reconnectTimer = window.setTimeout(() => { this.reconnectTimer = null; void this.connect(); }, wait);
  }

  private async connect() {
    if (this.stopped) return;
    this.controller?.abort();
    const controller = new AbortController(); this.controller = controller;
    try {
      const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(this.config.projectId)}/events`, {
        headers: { authorization: `Bearer ${this.config.token}` }, cache: 'no-store', credentials: 'omit', signal: controller.signal,
      });
      if (!response.ok || !response.body) throw new Error(`Stream indisponível (${response.status}).`);
      this.reconnectDelay = 1500; this.config.onConnection?.(true);
      const reader = response.body.getReader(); const decoder = new TextDecoder(); let buffer = '';
      while (!this.stopped) {
        const chunk = await reader.read(); if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true }).replace(/\r\n/g, '\n');
        let boundary = buffer.indexOf('\n\n');
        while (boundary >= 0) {
          const frame = buffer.slice(0, boundary); buffer = buffer.slice(boundary + 2); this.consumeFrame(frame);
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch (error) {
      if (!controller.signal.aborted && !this.stopped) this.config.onConnection?.(false);
    } finally {
      if (this.controller === controller) this.controller = null;
      if (!this.stopped) this.scheduleReconnect();
    }
  }

  private consumeFrame(frame: string) {
    if (!frame || frame.startsWith(':')) return;
    let type = 'message'; const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) type = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    }
    if (dataLines.length === 0) return;
    try {
      const parsed = JSON.parse(dataLines.join('\n')) as Record<string, unknown>;
      if (type === 'presence') this.config.onPresence?.(normalizePresence(parsed.users));
      if (type === 'ready' && Array.isArray(parsed.users)) this.config.onPresence?.(normalizePresence(parsed.users));
      this.config.onEvent?.({ type, data: parsed });
    } catch { /* quadro inválido é ignorado sem interromper o stream */ }
  }

  private async sendPresence() {
    if (this.stopped || !navigator.onLine) return;
    try {
      const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(this.config.projectId)}/presence`, {
        method: 'POST', headers: { authorization: `Bearer ${this.config.token}`, 'content-type': 'application/json' },
        body: JSON.stringify(this.presence), cache: 'no-store', credentials: 'omit',
      });
      if (!response.ok) return;
      const payload = await response.json() as { users?: unknown };
      this.config.onPresence?.(normalizePresence(payload.users));
    } catch { /* presença é best-effort e nunca bloqueia a edição */ }
  }
}
